import {
  Building2, Rocket, Receipt, Calculator, BookOpen, Users, LineChart,
  ShieldCheck, Sparkles, Globe, Utensils, Award, FileText, Building, TrendingUp,
} from "lucide-react";

const IMG = (id) =>
  `https://static.prod-images.emergentagent.com/jobs/168aab43-a5cf-4ad9-aeeb-bf2abf53e43a/images/${id}.jpeg`;

export const SERVICE_IMAGES = {
  "business-registration": IMG("9bc1c65513122ce6a7ee5686cc5096f3ccbfbf12546d25a3349e9e6d7c4c9e1e"),
  "startup-services": IMG("c206a90c6b2bc5ef1675a3494145c36fa8e1f2805aadf760155871ca9f734649"),
  "gst": IMG("03617f722e039682f5bf8efd409dfff1830c2fd56b30264a758fae502b61ab3c"),
  "income-tax": IMG("ab0a3bbc9bd802111cce37bcd6b4708b5eb1a8fe624b44c6d2e7429d55601f93"),
  "accounting": IMG("d3e05898f35b2a5a388a3d9e931dfe1f61196f4dc7b5e57e3bb9d459532bdf82"),
  "payroll": IMG("1f9e80e1f339cf53aac81844a7241f229d6e2ecce3f3abec051f39b64e55e817"),
  "virtual-cfo": IMG("79cd166b2ef884bcc9528b4feba33a49af361b9d7c3f0275e771bf62514f9a75"),
  "roc-compliance": IMG("b91f553a7855f2152de64357b152aa0ce5c0bfc40a02e81e4eeed1493adb162f"),
  "trademark": IMG("f1ad432402299169ea319a8896d697b513da56de62aae97ffd1a7f07ba2b00af"),
  "import-export-iec": IMG("b82004d0883387db0cf874c6ba019c68c1abeb1abb6913639be8b34b035a4dc3"),
  "fssai": IMG("8a36e4db138711848b0617c794fabfa78aafa3bb1e1c0bf2aac7c2e8cf728e69"),
  "iso": IMG("391f08f3fc3b2f040858a7c516f7e2aa75c78d4113cb08a75869f738b6017d29"),
  "legal-advisory": IMG("758ef36ef8cdf38dec1f97a0f98b2fab7efaeb4f2b45d4d8eff3c2cdd4b373bb"),
  "virtual-office": IMG("e6abe0ae9e0380c1333ea71eba231d61365d2bae502de2f70c988549b5b91474"),
  "business-consulting": IMG("ddc4f77eb05b1c7abde881d5ebdf8a661ce1c2390ab69d3b15bc8edd1a63c39f"),
};

export const CATEGORIES = [
  "Registration & Startup",
  "Taxation",
  "Finance & Payroll",
  "Legal & IP",
  "Licences & Certification",
  "Advisory",
];

export const SERVICES = [
  {
    slug: "business-registration",
    title: "Business Registration",
    category: "Registration & Startup",
    icon: Building2,
    tagline: "Start right — with the entity your ambition deserves.",
    short: "Company, LLP, OPC & partnership setup, handled end to end.",
    intro:
      "Private Limited, LLP, OPC, Partnership, Proprietorship, Section 8 or Producer Company — we run the full incorporation: name approval, DIN, digital signatures, MoA/AoA drafting and post-incorporation compliance, so day one is a clean start.",
    timeline: "7–12 business days (Pvt Ltd) · 10–15 (LLP)",
    govtFees: "₹1,000 – ₹9,000 (varies by capital)",
    profFees: "From ₹6,999",
    benefits: [
      "Limited liability protection",
      "Investor & bank-loan friendly structure",
      "Perpetual succession",
      "Better brand credibility",
      "Tax planning & deductions",
    ],
    eligibility: [
      "At least 2 directors/partners for Pvt Ltd/LLP (1 for OPC)",
      "Indian resident director required",
      "Valid PAN & Aadhaar",
      "Registered office proof",
    ],
    documents: [
      "PAN & Aadhaar of directors",
      "Passport-size photos",
      "Address proof (utility bill < 2 months)",
      "Rent agreement + NOC of office",
      "Latest bank statement / utility bill of office",
    ],
    steps: [
      "Free consultation & entity selection",
      "DSC & DIN application",
      "Name approval via RUN / SPICe+",
      "Drafting MoA, AoA, subscriber sheets",
      "Filing SPICe+ with MCA",
      "Certificate of Incorporation + PAN + TAN",
      "Post-incorporation setup: bank account, GST, MSME",
    ],
    deliverables: [
      "Certificate of Incorporation",
      "PAN & TAN",
      "MoA & AoA",
      "Digital signatures",
      "Master data pack",
    ],
  },
  {
    slug: "startup-services",
    title: "Startup & DPIIT Services",
    category: "Registration & Startup",
    icon: Rocket,
    tagline: "Every benefit the Startup India regime offers — claimed properly.",
    short: "DPIIT recognition, Seed Fund, pitch decks & investor readiness.",
    intro:
      "From DPIIT Startup India recognition to Seed Fund access and the 80-IAC tax exemption, we help you unlock every benefit meant for Indian startups. Our team also builds investor-grade pitch decks, financial models and cap tables.",
    timeline: "5–20 days (recognition) · 8–12 weeks (80-IAC)",
    govtFees: "Nil for DPIIT",
    profFees: "From ₹4,999",
    benefits: [
      "3-year tax holiday (80-IAC)",
      "Angel tax exemption",
      "IPR fast-track & rebates",
      "Access to Seed Fund",
      "Self-certification on 9 labour laws",
    ],
    eligibility: [
      "Entity less than 10 years old",
      "Turnover under ₹100 Cr in any year",
      "Innovation, improvement or scalability with employment potential",
    ],
    documents: [
      "Incorporation certificate",
      "PAN",
      "Website / pitch link",
      "Founder details",
      "Brief on product or service",
    ],
    steps: [
      "Eligibility & documentation review",
      "DPIIT profile creation",
      "Application filing",
      "Query response & liaison",
      "Certificate issuance",
    ],
    deliverables: [
      "DPIIT recognition certificate",
      "Advisory memo on tax & IP benefits",
      "Optional: pitch deck & CMA report",
    ],
  },
  {
    slug: "gst",
    title: "GST",
    category: "Taxation",
    icon: Receipt,
    tagline: "Registration, returns, refunds & advisory — the full lifecycle.",
    short: "End-to-end GST: registration, returns, refunds & notices.",
    intro:
      "The complete GST lifecycle — registration, monthly and quarterly returns (GSTR-1, 3B, IFF, 9, 9C), e-invoicing, e-way bill support, refunds for exports and inverted duty, and notice handling. Plus advisory on reverse charge, input tax credit and place of supply.",
    timeline: "3–7 business days for registration",
    govtFees: "Nil",
    profFees: "Registration ₹1,499 · Monthly returns from ₹999/mo",
    benefits: [
      "Legal recognition as supplier",
      "Seamless input tax credit flow",
      "Access to inter-state trade",
      "Composition scheme options",
      "Refund of accumulated ITC",
    ],
    eligibility: [
      "Aggregate turnover above ₹40L (goods) / ₹20L (services)",
      "Inter-state supply",
      "E-commerce operators",
      "Casual taxable persons",
    ],
    documents: [
      "PAN & Aadhaar of proprietor/directors",
      "Business PAN & incorporation certificate",
      "Address proof of principal place of business",
      "Bank statement / cancelled cheque",
      "DSC (for companies/LLPs)",
    ],
    steps: [
      "Documentation & category selection",
      "TRN generation",
      "Filing REG-01",
      "Query response",
      "GSTIN issuance",
    ],
    deliverables: [
      "GSTIN certificate",
      "Portal login credentials",
      "Return filing calendar",
      "ITC reconciliation reports",
    ],
  },
  {
    slug: "income-tax",
    title: "Income Tax",
    category: "Taxation",
    icon: Calculator,
    tagline: "Filing, notices, planning & appeals — for every taxpayer.",
    short: "ITR filing, notice handling, planning & appeals.",
    intro:
      "ITR filing for salaried individuals, freelancers, businesses, NRIs and HUFs — including capital gains, foreign income and crypto reporting. We also handle notices (139(9), 143(1), 148), assessments, appeals and forward-looking tax planning.",
    timeline: "Same-day to 2 days (ITR) · 15–45 days (notice response)",
    govtFees: "Nil (till due date)",
    profFees: "Salaried ₹499 · Business/CG from ₹1,999",
    benefits: [
      "Loan & visa readiness",
      "Refund maximisation",
      "Carry-forward of losses",
      "Avoid late fees & penalties",
      "Peace of mind at scrutiny",
    ],
    eligibility: [
      "Individuals, HUFs, firms, LLPs, companies, trusts",
      "NRIs with Indian-sourced income",
    ],
    documents: [
      "PAN, Aadhaar, bank details",
      "Form 16 / 16A",
      "AIS / TIS from portal",
      "Capital gains statements",
      "Home-loan & 80C proofs",
    ],
    steps: [
      "Data collection & AIS reconciliation",
      "Regime selection (old vs new)",
      "ITR preparation",
      "Client review",
      "E-filing & e-verification",
    ],
    deliverables: [
      "Acknowledgement (ITR-V)",
      "Computation sheet",
      "Advisory note on next-year planning",
    ],
  },
  {
    slug: "accounting",
    title: "Accounting & Bookkeeping",
    category: "Finance & Payroll",
    icon: BookOpen,
    tagline: "Cloud-first books, audit-ready all 365 days.",
    short: "Cloud bookkeeping with monthly closing, MIS & reconciliations.",
    intro:
      "Zoho, QuickBooks or Tally-based cloud bookkeeping with monthly closing, MIS reporting, GST reconciliation, TDS reconciliation and audit-ready books. Built for startups, agencies, e-commerce sellers, professionals and SMEs.",
    timeline: "Onboarding in 3 days · monthly closure by the 10th",
    govtFees: "N/A",
    profFees: "Starter ₹2,499/mo · Growth ₹6,999/mo · Scale ₹14,999/mo",
    benefits: [
      "Real-time financial visibility",
      "Audit-ready round the year",
      "Bank & GST reconciliations",
      "Investor-grade MIS",
      "Cost centres & project P&L",
    ],
    eligibility: ["All businesses maintaining books under Section 44AA"],
    documents: [
      "Bank statements",
      "Sales & purchase invoices",
      "Expense bills",
      "Previous year trial balance / financials",
    ],
    steps: [
      "Chart of accounts setup",
      "Cloud tool onboarding",
      "Transaction categorisation",
      "Reconciliation",
      "MIS delivery",
    ],
    deliverables: [
      "Monthly P&L, Balance Sheet, Cashflow",
      "Ratio & KPI dashboard",
      "Audit-ready trial balance",
    ],
  },
  {
    slug: "payroll",
    title: "Payroll",
    category: "Finance & Payroll",
    icon: Users,
    tagline: "Structured salaries. Statutory compliance. Zero disputes.",
    short: "Salary structuring, PF, ESIC, PT & full payroll compliance.",
    intro:
      "Tax-efficient salary structuring, monthly payroll processing, payslip generation, PF/ESIC/PT compliance, Form-16 and 24Q returns. HRIS-ready, with reimbursements, LOP and full & final settlements handled.",
    timeline: "Monthly cycle: cutoff 25th → payslips by the 1st",
    govtFees: "As per statute",
    profFees: "From ₹149 per employee per month",
    benefits: [
      "Tax-optimal salary structure",
      "Timely statutory compliance",
      "Zero payroll disputes",
      "Automated payslips",
      "Investor & audit ready",
    ],
    eligibility: ["Any employer with 1 or more employees"],
    documents: [
      "Employee master (CTC breakup)",
      "Attendance & leave data",
      "PF/ESIC codes",
      "Prior payroll data (if any)",
    ],
    steps: [
      "Employee onboarding",
      "CTC structuring",
      "Monthly inputs collection",
      "Payroll processing",
      "Statutory challans & returns",
    ],
    deliverables: ["Payslips", "PF/ESIC challans", "TDS 24Q returns", "Form-16 annually"],
  },
  {
    slug: "virtual-cfo",
    title: "Virtual CFO",
    category: "Finance & Payroll",
    icon: LineChart,
    tagline: "Board-grade finance leadership, at a fraction of the cost.",
    short: "MIS, cashflow forecasting & investor-ready reporting.",
    intro:
      "Fractional CFO services for growth-stage businesses — MIS, cashflow forecasting, unit economics, board decks, fundraise support, banking and vendor negotiations. All the finance leadership of a full-time CFO, without the full-time price.",
    timeline: "Onboarding in 7 days · monthly & quarterly cadence",
    govtFees: "N/A",
    profFees: "From ₹35,000/month",
    benefits: [
      "Board-ready reporting",
      "Fundraise-ready financial models",
      "Working-capital optimisation",
      "KPI & OKR governance",
      "M&A & valuation support",
    ],
    eligibility: ["Businesses with ₹1 Cr+ revenue or funded startups"],
    documents: ["Historical financials", "Business plan", "Cap table & term sheets"],
    steps: [
      "Diagnostic assessment",
      "Reporting stack setup",
      "Monthly MIS & review",
      "Quarterly board deck",
      "Ad-hoc CFO support",
    ],
    deliverables: [
      "Monthly MIS pack",
      "13-week rolling cashflow forecast",
      "Board deck & KPI dashboard",
    ],
  },
  {
    slug: "roc-compliance",
    title: "ROC Compliance",
    category: "Legal & IP",
    icon: ShieldCheck,
    tagline: "Squeaky clean at the MCA — every single year.",
    short: "Annual filings, DIR-3 KYC, resolutions & event-based forms.",
    intro:
      "Annual ROC filings (AOC-4, MGT-7/7A), DIR-3 KYC, board and AGM minutes, ADT-1, event-based filings (share transfers, allotments, charge creation), DPT-3 and MSME-1. We keep your company in the MCA's good books, permanently.",
    timeline: "Annual: within 30/60 days of AGM · KYC by 30 Sept",
    govtFees: "As per authorised capital",
    profFees: "Starter pack ₹6,999/year",
    benefits: [
      "Avoid ₹100/day penalty",
      "Director disqualification protection",
      "Company stays 'active'",
      "Ready for funding due diligence",
    ],
    eligibility: ["All Pvt Ltd, OPC, LLP & Public companies"],
    documents: [
      "Financial statements",
      "Board resolutions",
      "Auditor's report",
      "Shareholding pattern",
    ],
    steps: [
      "Compliance calendar preparation",
      "Documentation",
      "Filing on MCA V3",
      "SRN confirmation",
    ],
    deliverables: ["Filing acknowledgements", "Minutes & registers", "Compliance calendar"],
  },
  {
    slug: "trademark",
    title: "Trademark",
    category: "Legal & IP",
    icon: Sparkles,
    tagline: "Your brand, protected across all 45 classes.",
    short: "Search, filing, objection responses & renewals.",
    intro:
      "Public search, class selection, TM-A filing, responses to examination reports, opposition proceedings, hearings and renewals every 10 years. We protect the name your business is building under, across all 45 classes.",
    timeline: "Filing in 1–3 days · registration in 18–24 months",
    govtFees: "₹4,500 (individual/MSME) · ₹9,000 (others)",
    profFees: "From ₹1,499",
    benefits: [
      "Exclusive rights to your brand",
      "® symbol post-registration",
      "Legal remedy for infringement",
      "Assignable & licensable asset",
    ],
    eligibility: ["Individuals, startups, MSMEs, LLPs, companies"],
    documents: [
      "Logo (JPG/PDF)",
      "Applicant KYC",
      "Udyam certificate (for concessional fee)",
      "Power of Attorney (TM-48)",
    ],
    steps: [
      "Public search",
      "Class & specification finalisation",
      "TM-A filing",
      "Journal publication",
      "Registration certificate",
    ],
    deliverables: [
      "Filing receipt with TM number",
      "™ usage rights immediately",
      "Examination reply drafting",
    ],
  },
  {
    slug: "import-export-iec",
    title: "Import Export & IEC",
    category: "Licences & Certification",
    icon: Globe,
    tagline: "One code. Global markets.",
    short: "IEC code, DGFT compliance & export incentives.",
    intro:
      "Importer-Exporter Code issuance in a day, DGFT compliance, RCMC through export promotion councils, RoDTEP/RoSCTL claims, EPCG and Advance Authorisation, plus SEIS/MEIS advisory — everything your cross-border trade needs.",
    timeline: "1 business day",
    govtFees: "₹500",
    profFees: "From ₹1,499",
    benefits: [
      "Global market access",
      "Export incentives up to 7% of FOB",
      "Foreign exchange facilitation",
    ],
    eligibility: ["Any business with a PAN and bank account"],
    documents: [
      "PAN",
      "Address proof",
      "Bank certificate / cancelled cheque",
      "Digital signature",
    ],
    steps: ["DGFT account creation", "ANF-2A filing", "IEC issuance"],
    deliverables: ["IEC certificate with 10-digit code"],
  },
  {
    slug: "fssai",
    title: "FSSAI Licence",
    category: "Licences & Certification",
    icon: Utensils,
    tagline: "Basic, State or Central — sorted, with renewals.",
    short: "Food licences for every scale of food business.",
    intro:
      "Basic Registration, State and Central licences for food business operators — restaurants, cloud kitchens, manufacturers, importers, transporters and e-commerce sellers — plus timely renewals so the licence never lapses.",
    timeline: "7–15 business days",
    govtFees: "₹100 – ₹7,500 per year",
    profFees: "From ₹1,999",
    benefits: [
      "Legal mandate compliance",
      "Consumer trust",
      "Higher retail acceptance",
      "Export enablement",
    ],
    eligibility: [
      "All food business operators; category depends on turnover & scale",
    ],
    documents: [
      "Business KYC",
      "Premises proof",
      "Product list",
      "Water test report (for manufacturers)",
    ],
    steps: [
      "Category assessment",
      "Form A/B filing",
      "Query response",
      "Inspection (if applicable)",
      "Licence issuance",
    ],
    deliverables: ["Digital FSSAI certificate with 14-digit licence number"],
  },
  {
    slug: "iso",
    title: "ISO Certification",
    category: "Licences & Certification",
    icon: Award,
    tagline: "9001, 27001, 22000 & more — audit-ready.",
    short: "Certification via accredited bodies, documentation included.",
    intro:
      "ISO 9001, 14001, 27001, 22000, 45001 and more — through accredited certification bodies. We handle the gap analysis, documentation, internal audit and stage-1/stage-2 audits end to end.",
    timeline: "10–45 days depending on scope",
    govtFees: "N/A",
    profFees: "From ₹6,999",
    benefits: [
      "Tender eligibility",
      "Process discipline",
      "Global recognition",
      "Customer confidence",
    ],
    eligibility: ["Any organisation, of any size"],
    documents: ["Business registration", "Process flow", "Manuals (we draft these)"],
    steps: [
      "Gap analysis",
      "Documentation",
      "Awareness training",
      "Internal audit",
      "Certification audit",
      "Certificate",
    ],
    deliverables: ["ISO certificate valid for 3 years"],
  },
  {
    slug: "legal-advisory",
    title: "Legal Advisory",
    category: "Legal & IP",
    icon: FileText,
    tagline: "Contracts that close deals, not raise questions.",
    short: "Contracts, notices, agreements & litigation support.",
    intro:
      "Drafting and vetting of contracts, NDAs, ESOP policies, shareholders' agreements, employment contracts, founder agreements, notices and replies — with litigation support through partner advocates when it's needed.",
    timeline: "24–72 hours per contract",
    govtFees: "N/A",
    profFees: "From ₹2,499 per contract",
    benefits: [
      "Risk mitigation",
      "Enforceability",
      "Faster deal closures",
      "Legal peace of mind",
    ],
    eligibility: ["Businesses, professionals & individuals"],
    documents: ["Existing draft (if any)", "Party details", "Commercial terms"],
    steps: [
      "Requirement scoping",
      "Drafting",
      "Client review",
      "Party negotiation support",
      "Execution assistance",
    ],
    deliverables: ["Final signed drafts", "Version-tracked reviews"],
  },
  {
    slug: "virtual-office",
    title: "Virtual Office",
    category: "Registration & Startup",
    icon: Building,
    tagline: "A premium address that works as hard as you do.",
    short: "Prime business address for GST & registrations.",
    intro:
      "Premium virtual office addresses in Delhi NCR and metros — usable for GST registration, company incorporation, mail handling and meeting-room bookings. Fully compliant, with NOC and utility bill included.",
    timeline: "24 hours",
    govtFees: "N/A",
    profFees: "From ₹999/month",
    benefits: [
      "Prestigious business address",
      "GST across states without physical presence",
      "Mail handling",
      "On-demand meeting rooms",
    ],
    eligibility: ["Any business needing an address for compliance or branding"],
    documents: ["Applicant KYC", "Signed service agreement"],
    steps: ["Location selection", "Agreement", "Address kit (NOC, rent agreement, utility bill)"],
    deliverables: ["NOC", "Rent agreement", "Utility bill", "Signage"],
  },
  {
    slug: "business-consulting",
    title: "Business Consulting",
    category: "Advisory",
    icon: TrendingUp,
    tagline: "Sharper decisions, backed by board-grade analysis.",
    short: "Growth strategy, M&A, valuations & due diligence.",
    intro:
      "Strategy consulting for growth-stage businesses — market entry, unit-economics improvement, M&A, valuation (DCF and comparables), due diligence and process transformation. Analysis your board can act on.",
    timeline: "2–8 weeks per engagement",
    govtFees: "N/A",
    profFees: "From ₹75,000 per engagement",
    benefits: [
      "Sharper decisions",
      "Board-grade analysis",
      "Objective third-party view",
      "Investor readiness",
    ],
    eligibility: ["Founders, boards & investors"],
    documents: ["Business overview", "Financials", "Data-room access"],
    steps: [
      "Kick-off & scoping",
      "Data & interviews",
      "Analysis & modelling",
      "Strategy recommendation",
      "Implementation support",
    ],
    deliverables: ["Board-grade report", "Financial models", "Roadmap"],
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);

export const relatedServices = (slug) => {
  const service = getService(slug);
  if (!service) return [];
  const same = SERVICES.filter((s) => s.category === service.category && s.slug !== slug);
  const others = SERVICES.filter((s) => s.category !== service.category);
  return [...same, ...others].slice(0, 3);
};
